import { APIResponse, apiService } from "../api";

export interface MenuItem {
  id: string;
  name: string;
  name_ar: string;
  price: number;
  category_id: string;
  category?: MenuCategory;
  description?: string;
  description_ar?: string;
  image?: string;
  questions?: any[];
}

export interface MenuCategory {
  id: string;
  name: string;
  name_ar?: string;
}

export interface MenuQueryParams {
  pageNumber?: number;
  limit?: number;
  category_ids?: string;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface CategoryQueryParams {
  pageNumber?: number;
  limit?: number;
  search?: string;
}

class MenuService {
  private readonly baseUrl = "/buffets/v1/landing";

  // All methods return APIResponse<T> which is { rows: T[], count: number }

  async getMenuItems(params?: MenuQueryParams): Promise<APIResponse<MenuItem>> {
    const queryParams = {
      pageNumber: params?.pageNumber || 1,
      limit: params?.limit || 100,
      ...(params?.category_ids && { category_ids: params.category_ids }),
      ...(params?.search && { search: params.search }),
      ...(params?.sort_by && { sort_by: params.sort_by }),
      ...(params?.sort_order && { sort_order: params.sort_order }),
    };
    return apiService.get<MenuItem>(`${this.baseUrl}/items`, {
      params: queryParams,
    });
  }

  async getMenuItemsByCategory(
    categoryId: string,
    params?: Omit<MenuQueryParams, "category_ids">,
  ): Promise<APIResponse<MenuItem>> {
    return this.getMenuItems({ ...params, category_ids: categoryId });
  }

  async getMenuItem(id: string): Promise<APIResponse<MenuItem>> {
    return apiService.get<MenuItem>(`${this.baseUrl}/items/${id}`);
  }

  async getCategories(
    params?: CategoryQueryParams,
  ): Promise<APIResponse<MenuCategory>> {
    const queryParams = {
      pageNumber: params?.pageNumber || 1,
      limit: params?.limit || 100,
      ...(params?.search && { search: params.search }),
    };
    return apiService.get<MenuCategory>(`${this.baseUrl}/categories`, {
      params: queryParams,
    });
  }

  async getCategory(id: string): Promise<APIResponse<MenuCategory>> {
    return apiService.get<MenuCategory>(`${this.baseUrl}/categories/${id}`);
  }

  async searchMenuItems(
    query: string,
    categoryId?: string,
  ): Promise<APIResponse<MenuItem>> {
    return this.getMenuItems({
      search: query,
      category_ids: categoryId,
      pageNumber: 1,
      limit: 50,
    });
  }

  async getPaginatedItems(
    page: number = 1,
    limit: number = 10,
    categoryId?: string,
  ): Promise<APIResponse<MenuItem>> {
    return this.getMenuItems({
      pageNumber: page,
      limit,
      category_ids: categoryId,
    });
  }

  async getPaginatedCategories(
    page: number = 1,
    limit: number = 10,
  ): Promise<APIResponse<MenuCategory>> {
    return this.getCategories({ pageNumber: page, limit });
  }

  // Admin methods
  async createMenuItem(
    data: Partial<MenuItem>,
  ): Promise<APIResponse<MenuItem>> {
    return apiService.post<MenuItem>(`${this.baseUrl}/items`, data);
  }

  async updateMenuItem(
    id: string,
    data: Partial<MenuItem>,
  ): Promise<APIResponse<MenuItem>> {
    return apiService.patch<MenuItem>(`${this.baseUrl}/items/${id}`, data);
  }

  async deleteMenuItem(id: string): Promise<APIResponse<MenuItem>> {
    return apiService.delete<MenuItem>(`${this.baseUrl}/items/${id}`);
  }

  async createCategory(
    data: Partial<MenuCategory>,
  ): Promise<APIResponse<MenuCategory>> {
    return apiService.post<MenuCategory>(`${this.baseUrl}/categories`, data);
  }

  async updateCategory(
    id: string,
    data: Partial<MenuCategory>,
  ): Promise<APIResponse<MenuCategory>> {
    return apiService.patch<MenuCategory>(
      `${this.baseUrl}/categories/${id}`,
      data,
    );
  }

  async deleteCategory(id: string): Promise<APIResponse<MenuCategory>> {
    return apiService.delete<MenuCategory>(`${this.baseUrl}/categories/${id}`);
  }

  async uploadItemImage(
    id: string,
    file: File,
    onProgress?: (p: number) => void,
  ): Promise<APIResponse<any>> {
    const formData = new FormData();
    formData.append("image", file);
    return apiService.upload<any>(
      `${this.baseUrl}/items/${id}/image`,
      formData,
      (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    );
  }

  async uploadCategoryImage(
    id: string,
    file: File,
    onProgress?: (p: number) => void,
  ): Promise<APIResponse<any>> {
    const formData = new FormData();
    formData.append("image", file);
    return apiService.upload<any>(
      `${this.baseUrl}/categories/${id}/image`,
      formData,
      (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    );
  }

  async updateItemsOrder(
    updates: Array<{ id: string; order: number }>,
  ): Promise<APIResponse<MenuItem>> {
    return apiService.post<MenuItem>(`${this.baseUrl}/items/reorder`, {
      items: updates,
    });
  }

  async updateCategoriesOrder(
    updates: Array<{ id: string; order: number }>,
  ): Promise<APIResponse<MenuCategory>> {
    return apiService.post<MenuCategory>(`${this.baseUrl}/categories/reorder`, {
      categories: updates,
    });
  }
}

const menuService = new MenuService();
export default menuService;

// import { APIResponse, apiService } from "../api";

// /**
//  * Menu Item Interface (Bridge X Global Buffet API)
//  */
// export interface MenuItem {
//   id: string;
//   name: string;
//   name_ar: string;
//   price: number;
//   category_id: string;
//   category?: MenuCategory;
//   description?: string;
//   description_ar?: string;
//   image?: string;
//   images?: string[];
//   available?: boolean;
//   is_active?: boolean;
//   tags?: string[];
//   order?: number;
//   created_at?: string;
//   updated_at?: string;
// }

// /**
//  * Menu Category Interface (Bridge X Global Buffet API)
//  */
// export interface MenuCategory {
//   id: string;
//   name: string;
//   name_ar: string;
//   description?: string;
//   description_ar?: string;
//   image?: string;
//   order?: number;
//   is_active?: boolean;
//   items_count?: number;
//   created_at?: string;
//   updated_at?: string;
// }

// /**
//  * Menu Query Parameters
//  */
// export interface MenuQueryParams {
//   pageNumber?: number;
//   limit?: number;
//   category_ids?: string; // Single or comma-separated category IDs
//   search?: string;
//   is_active?: boolean;
//   sort_by?: string;
//   sort_order?: "asc" | "desc";
// }

// /**
//  * Category Query Parameters
//  */
// export interface CategoryQueryParams {
//   pageNumber?: number;
//   limit?: number;
//   search?: string;
//   is_active?: boolean;
// }

// /**
//  * Menu Service
//  * Handles all menu/buffet-related API operations for Bridge X Global
//  * Base URL: https://offices-backend.bridge-xglobal.com/buffets/v1/dashboard
//  */
// class MenuService {
//   private readonly baseUrl = "/buffets/v1/landing";

//   /**
//    * Get all menu items with optional filters
//    * Endpoint: GET /buffets/v1/dashboard/items
//    *
//    * @param params - Query parameters for filtering and pagination
//    * @example
//    * // Get items from specific category
//    * menuService.getMenuItems({
//    *   category_ids: '6d641ea7-f9c0-496a-93ef-93d2c47f7501',
//    *   pageNumber: 1,
//    *   limit: 10
//    * });
//    */
//   async getMenuItems(
//     params?: MenuQueryParams,
//   ): Promise<APIResponse<MenuItem[]>> {
//     const queryParams = {
//       pageNumber: params?.pageNumber || 1,
//       limit: params?.limit || 100,
//       ...(params?.category_ids && { category_ids: params.category_ids }),
//       ...(params?.search && { search: params.search }),
//       ...(params?.sort_by && { sort_by: params.sort_by }),
//       ...(params?.sort_order && { sort_order: params.sort_order }),
//     };

//     return apiService.get<MenuItem[]>(`${this.baseUrl}/items`, {
//       params: queryParams,
//     });
//   }

//   /**
//    * Get menu items by single category ID
//    *
//    * @param categoryId - Category UUID
//    * @param params - Additional query parameters
//    */
//   async getMenuItemsByCategory(
//     categoryId: string,
//     params?: Omit<MenuQueryParams, "category_ids">,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return this.getMenuItems({
//       ...params,
//       category_ids: categoryId,
//     });
//   }

//   /**
//    * Get menu items by multiple category IDs
//    *
//    * @param categoryIds - Array of category UUIDs
//    * @param params - Additional query parameters
//    */
//   async getMenuItemsByCategories(
//     categoryIds: string[],
//     params?: Omit<MenuQueryParams, "category_ids">,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return this.getMenuItems({
//       ...params,
//       category_ids: categoryIds.join(","),
//     });
//   }

//   /**
//    * Get a single menu item by ID
//    *
//    * @param id - Item UUID
//    */
//   async getMenuItem(id: string): Promise<APIResponse<MenuItem>> {
//     return apiService.get<MenuItem>(`${this.baseUrl}/items/${id}`);
//   }

//   /**
//    * Get all menu categories
//    * Endpoint: GET /buffets/v1/dashboard/categories
//    *
//    * @param params - Query parameters for filtering and pagination
//    * @example
//    * menuService.getCategories({ pageNumber: 1, limit: 10 });
//    */
//   async getCategories(
//     params?: CategoryQueryParams,
//   ): Promise<APIResponse<MenuCategory[]>> {
//     const queryParams = {
//       pageNumber: params?.pageNumber || 1,
//       limit: params?.limit || 100,
//       ...(params?.search && { search: params.search }),
//     };

//     return apiService.get<MenuCategory[]>(`${this.baseUrl}/categories`, {
//       params: queryParams,
//     });
//   }

//   /**
//    * Get a single category by ID
//    *
//    * @param id - Category UUID
//    */
//   async getCategory(id: string): Promise<APIResponse<MenuCategory>> {
//     return apiService.get<MenuCategory>(`${this.baseUrl}/categories/${id}`);
//   }

//   /**
//    * Search menu items
//    *
//    * @param query - Search query string
//    * @param categoryId - Optional category filter
//    */
//   async searchMenuItems(
//     query: string,
//     categoryId?: string,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return this.getMenuItems({
//       search: query,
//       category_ids: categoryId,
//       pageNumber: 1,
//       limit: 50,
//     });
//   }

//   /**
//    * Get only active menu items
//    * Note: API doesn't support is_active filter, returns all items
//    *
//    * @param params - Additional query parameters
//    */
//   async getActiveItems(
//     params?: Omit<MenuQueryParams, "is_active">,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return this.getMenuItems(params);
//   }

//   /**
//    * Get only active categories
//    * Note: API doesn't support is_active filter, returns all categories
//    *
//    * @param params - Additional query parameters
//    */
//   async getActiveCategories(
//     params?: Omit<CategoryQueryParams, "is_active">,
//   ): Promise<APIResponse<MenuCategory[]>> {
//     return this.getCategories(params);
//   }

//   /**
//    * Get paginated menu items
//    *
//    * @param page - Page number (starts from 1)
//    * @param limit - Items per page
//    * @param categoryId - Optional category filter
//    */
//   async getPaginatedItems(
//     page: number = 1,
//     limit: number = 10,
//     categoryId?: string,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return this.getMenuItems({
//       pageNumber: page,
//       limit,
//       category_ids: categoryId,
//     });
//   }

//   /**
//    * Get paginated categories
//    *
//    * @param page - Page number (starts from 1)
//    * @param limit - Items per page
//    */
//   async getPaginatedCategories(
//     page: number = 1,
//     limit: number = 10,
//   ): Promise<APIResponse<MenuCategory[]>> {
//     return this.getCategories({
//       pageNumber: page,
//       limit,
//     });
//   }

//   // ==================== ADMIN METHODS ====================

//   /**
//    * Create a new menu item (Admin only)
//    *
//    * @param data - Item data
//    */
//   async createMenuItem(
//     data: Omit<MenuItem, "id" | "created_at" | "updated_at">,
//   ): Promise<APIResponse<MenuItem>> {
//     return apiService.post<MenuItem>(`${this.baseUrl}/items`, data);
//   }

//   /**
//    * Update an existing menu item (Admin only)
//    *
//    * @param id - Item UUID
//    * @param data - Updated item data
//    */
//   async updateMenuItem(
//     id: string,
//     data: Partial<MenuItem>,
//   ): Promise<APIResponse<MenuItem>> {
//     return apiService.patch<MenuItem>(`${this.baseUrl}/items/${id}`, data);
//   }

//   /**
//    * Delete a menu item (Admin only)
//    *
//    * @param id - Item UUID
//    */
//   async deleteMenuItem(id: string): Promise<APIResponse<void>> {
//     return apiService.delete(`${this.baseUrl}/items/${id}`);
//   }

//   /**
//    * Create a new category (Admin only)
//    *
//    * @param data - Category data
//    */
//   async createCategory(
//     data: Omit<MenuCategory, "id" | "created_at" | "updated_at">,
//   ): Promise<APIResponse<MenuCategory>> {
//     return apiService.post<MenuCategory>(`${this.baseUrl}/categories`, data);
//   }

//   /**
//    * Update an existing category (Admin only)
//    *
//    * @param id - Category UUID
//    * @param data - Updated category data
//    */
//   async updateCategory(
//     id: string,
//     data: Partial<MenuCategory>,
//   ): Promise<APIResponse<MenuCategory>> {
//     return apiService.patch<MenuCategory>(
//       `${this.baseUrl}/categories/${id}`,
//       data,
//     );
//   }

//   /**
//    * Delete a category (Admin only)
//    *
//    * @param id - Category UUID
//    */
//   async deleteCategory(id: string): Promise<APIResponse<void>> {
//     return apiService.delete(`${this.baseUrl}/categories/${id}`);
//   }

//   /**
//    * Toggle menu item active status
//    *
//    * @param id - Item UUID
//    * @param isActive - Active status
//    */
//   async toggleItemStatus(
//     id: string,
//     isActive: boolean,
//   ): Promise<APIResponse<MenuItem>> {
//     return apiService.patch<MenuItem>(`${this.baseUrl}/items/${id}`, {
//       is_active: isActive,
//     });
//   }

//   /**
//    * Toggle category active status
//    *
//    * @param id - Category UUID
//    * @param isActive - Active status
//    */
//   async toggleCategoryStatus(
//     id: string,
//     isActive: boolean,
//   ): Promise<APIResponse<MenuCategory>> {
//     return apiService.patch<MenuCategory>(`${this.baseUrl}/categories/${id}`, {
//       is_active: isActive,
//     });
//   }

//   /**
//    * Upload menu item image
//    *
//    * @param id - Item UUID
//    * @param file - Image file
//    * @param onProgress - Progress callback
//    */
//   async uploadItemImage(
//     id: string,
//     file: File,
//     onProgress?: (progress: number) => void,
//   ): Promise<APIResponse<{ image_url: string; image: string }>> {
//     const formData = new FormData();
//     formData.append("image", file);

//     return apiService.upload<{ image_url: string; image: string }>(
//       `${this.baseUrl}/items/${id}/image`,
//       formData,
//       (progressEvent) => {
//         if (onProgress && progressEvent.total) {
//           const percentCompleted = Math.round(
//             (progressEvent.loaded * 100) / progressEvent.total,
//           );
//           onProgress(percentCompleted);
//         }
//       },
//     );
//   }

//   /**
//    * Upload category image
//    *
//    * @param id - Category UUID
//    * @param file - Image file
//    * @param onProgress - Progress callback
//    */
//   async uploadCategoryImage(
//     id: string,
//     file: File,
//     onProgress?: (progress: number) => void,
//   ): Promise<APIResponse<{ image_url: string; image: string }>> {
//     const formData = new FormData();
//     formData.append("image", file);

//     return apiService.upload<{ image_url: string; image: string }>(
//       `${this.baseUrl}/categories/${id}/image`,
//       formData,
//       (progressEvent) => {
//         if (onProgress && progressEvent.total) {
//           const percentCompleted = Math.round(
//             (progressEvent.loaded * 100) / progressEvent.total,
//           );
//           onProgress(percentCompleted);
//         }
//       },
//     );
//   }

//   /**
//    * Update items display order (bulk)
//    *
//    * @param updates - Array of item ID and order pairs
//    */
//   async updateItemsOrder(
//     updates: Array<{ id: string; order: number }>,
//   ): Promise<APIResponse<MenuItem[]>> {
//     return apiService.post<MenuItem[]>(`${this.baseUrl}/items/reorder`, {
//       items: updates,
//     });
//   }

//   /**
//    * Update categories display order (bulk)
//    *
//    * @param updates - Array of category ID and order pairs
//    */
//   async updateCategoriesOrder(
//     updates: Array<{ id: string; order: number }>,
//   ): Promise<APIResponse<MenuCategory[]>> {
//     return apiService.post<MenuCategory[]>(
//       `${this.baseUrl}/categories/reorder`,
//       {
//         categories: updates,
//       },
//     );
//   }
// }

// // Export singleton instance
// const menuService = new MenuService();
// export default menuService;
